import { useEffect, useRef } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListNode, ListItemNode } from '@lexical/list'
import { LinkNode } from '@lexical/link'
import { HeadingNode } from '@lexical/rich-text'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getRoot, $insertNodes, type EditorState } from 'lexical'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import ToolbarPlugin from './LexicalToolbar'

/**
 * Plugin to load initial HTML content (only once)
 */
function InitialContentPlugin({ html }: { html: string }) {
  const [editor] = useLexicalComposerContext()
  const initializedRef = useRef(false)
  const lastHtmlRef = useRef('')

  useEffect(() => {
    // Skip if already initialized or no html
    if (initializedRef.current || !html) return

    // Skip if html hasn't changed from empty
    if (lastHtmlRef.current === html) return

    initializedRef.current = true
    lastHtmlRef.current = html

    editor.update(() => {
      const parser = new DOMParser()
      const dom = parser.parseFromString(html, 'text/html')
      const nodes = $generateNodesFromDOM(editor, dom)

      // Clear existing content
      const root = $getRoot()
      root.clear()

      // Insert new nodes
      $insertNodes(nodes)
    })
  }, [editor, html]) // Depend on both editor and html

  return null
}

/**
 * Plugin to handle onChange and convert to HTML
 */
function OnChangeHtmlPlugin({ onChange }: { onChange: (html: string) => void }) {
  const [editor] = useLexicalComposerContext()

  const handleChange = (editorState: EditorState) => {
    editorState.read(() => {
      const htmlString = $generateHtmlFromNodes(editor)
      onChange(htmlString)
    })
  }

  return <OnChangePlugin onChange={handleChange} />
}

export interface LexicalEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  accentColor?: 'blue' | 'orange' | 'green' | 'purple'
  namespace?: string
}

/**
 * Lexical Rich Text Editor Component with Dark Mode Support
 */
export default function LexicalEditor({
  value,
  onChange,
  placeholder = 'Text eingeben...',
  accentColor = 'blue',
  namespace = 'WerkstattEditor'
}: LexicalEditorProps) {
  // Define accent colors for links and active buttons
  const accentColors = {
    blue: {
      link: 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300',
      button: 'text-blue-600 dark:text-blue-400',
      active: 'bg-blue-200 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
    },
    orange: {
      link: 'text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300',
      button: 'text-orange-600 dark:text-orange-400',
      active: 'bg-orange-200 dark:bg-orange-900 text-orange-900 dark:text-orange-100'
    },
    green: {
      link: 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300',
      button: 'text-green-600 dark:text-green-400',
      active: 'bg-green-200 dark:bg-green-900 text-green-900 dark:text-green-100'
    },
    purple: {
      link: 'text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300',
      button: 'text-purple-600 dark:text-purple-400',
      active: 'bg-purple-200 dark:bg-purple-900 text-purple-900 dark:text-purple-100'
    }
  }

  const colors = accentColors[accentColor]

  const initialConfig = {
    namespace,
    theme: {
      paragraph: 'mb-2',
      heading: {
        h1: 'text-3xl font-bold mb-4',
        h2: 'text-2xl font-bold mb-3',
        h3: 'text-xl font-bold mb-2',
      },
      list: {
        ul: 'list-disc list-inside mb-2',
        ol: 'list-decimal list-inside mb-2',
        listitem: 'ml-4',
      },
      link: `${colors.link} underline`,
      text: {
        bold: 'font-bold',
        italic: 'italic',
        underline: 'underline',
      },
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      LinkNode,
    ],
    onError: (error: Error) => {
      console.error('Lexical Error:', error)
    },
  }

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="border border-gray-300 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900">
        <ToolbarPlugin accentColor={accentColor} />
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="min-h-[200px] max-h-[500px] overflow-y-auto p-4 outline-none prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-gray-100"
                aria-placeholder={placeholder}
              />
            }
            placeholder={
              <div className="absolute top-4 left-4 text-gray-400 dark:text-gray-500 pointer-events-none select-none">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
      </div>

      <HistoryPlugin />
      <ListPlugin />
      <LinkPlugin />
      <InitialContentPlugin html={value} />
      <OnChangeHtmlPlugin onChange={onChange} />
    </LexicalComposer>
  )
}
